import { Status } from 'common/utils/constants';
import moment from 'moment';
import { updateSinglePageLocalLogList } from 'redux/actions';
import { IconAndText } from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import { reactTableSortOnClick } from 'common/utils/helpers';
import classnames from 'classnames';
import SvgAF from 'components/shared/SvgAF/SvgAF';

/**
 * event log snapshot can be truncated when there are too many events
 * this function is used to check if such snapshot is truncated
 * @param {object} eventlog
 * @returns {boolean} true if eventlog is truncated, otherwise false
 */
export const isEventLogTruncated = (eventlog) => {
  if (!eventlog) return false;

  return eventlog.truncated === true;
};

export const createSnapshotStatusMsg = {
  pending: Status.PENDING,
  tenSec: '10sec',
  thirtySec: '30sec',
  error: Status.FAILURE,
  success: Status.SUCCESS
};

export const errorCodeMsg = {
  403: 'Device is offline',
  404: 'Device Not Found',
  408: 'Request Timeout'
};

export const updateSortedLocalLogList = async (parent, data) => {
  if (!data || data?.length === 0) return;
  
  let localLogList = [];
  (await data) &&
    data.forEach((log) => {
      localLogList.push(log._original);
    });

  await parent.props.dispatch(updateSinglePageLocalLogList(localLogList));
};

export const createLogObjForSorting = (log) => {
  return {
    timestamp: log?.calendar_time && moment(log.calendar_time).format('MMM DD, YYYY HH:mm:ss'),
    timeSinceBoot: log?.time_since_boot ? log.time_since_boot : 'Unknown',
    name: log?.name,
    category: log?.category,
    message: log.message
  };
};

export const columns = (parent) => {
  return [
    {
      Header: (col) => {
        const colName =
          parent.props?.localLogList?.[0]?.calendar_time != null ? 'Timestamp' : 'Time since the last reboot';
        return (
          <div className="LogsTable_Header_OuterDiv" data-tip={colName}>
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={colName}
              reverse
              onClick={async () => {
                reactTableSortOnClick(parent, 'log_timestamp');
                updateSortedLocalLogList(parent, col.data);
              }}
            />
            <div className="LogsTableHeader_ResizerSliderIcon">
              <div className="LogsTableHeader_ResizerSliderIcon_Bar"></div>
            </div>
          </div>
        );
      },
      id: 'log_timestamp',
      minWidth: 75,
      accessor: (log) => {
        return log && createLogObjForSorting(log);
      },
      Cell: (row) => {
        return (
          <div className="LogsTable_cellWrapper" data-tip={row.value?.timestamp || row.value?.timeSinceBoot}>
            {row.value?.timestamp || row.value?.timeSinceBoot}
          </div>
        );
      },
      sortMethod: (a, b) => {
        const aVal = a.timestamp ?? a.timeSinceBoot;
        const bVal = b.timestamp ?? b.timeSinceBoot;
        if (aVal !== bVal) return aVal > bVal ? 1 : -1;
        if (a.name !== b.name) return a.name > b.name ? 1 : -1;
        if (a.category !== b.category) return a.category > b.category ? 1 : -1;
        if (a.message !== b.message) return a.message > b.message ? 1 : -1;
      }
    },
    {
      Header: (col) => {
        return (
          <div className="LogsTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'Name'}
              reverse
              onClick={async () => {
                reactTableSortOnClick(parent, 'log_name');
                updateSortedLocalLogList(parent, col.data);
              }}
            />
            <div className="LogsTableHeader_ResizerSliderIcon">
              <div className="LogsTableHeader_ResizerSliderIcon_Bar"></div>
            </div>
          </div>
        );
      },
      id: 'log_name',
      minWidth: 100,
      accessor: (log) => {
        return createLogObjForSorting(log);
      },
      Cell: (row) => {
        return (
          <div className="LogsTable_cellWrapper" data-tip={row.value.name}>
            {row.value.name}
          </div>
        );
      },
      sortMethod: (a, b) => {
        if (a.name !== b.name) return a.name > b.name ? 1 : -1;
        if (a.category !== b.category) return a.category > b.category ? 1 : -1;
        if (a.message !== b.message) return a.message > b.message ? 1 : -1;
        if (a.timeSinceBoot !== b.timeSinceBoot) return a.timeSinceBoot > b.timeSinceBoot ? 1 : -1;
      }
    },
    {
      Header: (col) => {
        return (
          <div className="LogsTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'Category'}
              reverse
              onClick={async () => {
                reactTableSortOnClick(parent, 'log_category');
                updateSortedLocalLogList(parent, col.data);
              }}
            />
            <div className="LogsTableHeader_ResizerSliderIcon">
              <div className="LogsTableHeader_ResizerSliderIcon_Bar"></div>
            </div>
          </div>
        );
      },
      id: 'log_category',
      minWidth: 60,
      accessor: (log) => {
        return createLogObjForSorting(log);
      },
      Cell: (row) => {
        return (
          <div className="LogsTable_cellWrapper" data-tip={row.value.category}>
            {row.value.category}
          </div>
        );
      },
      sortMethod: (a, b) => {
        if (a.category !== b.category) return a.category > b.category ? 1 : -1;
        if (a.message !== b.message) return a.message > b.message ? 1 : -1;
        if (a.timeSinceBoot !== b.timeSinceBoot) return a.timeSinceBoot > b.timeSinceBoot ? 1 : -1;
        if (a.name !== b.name) return a.name > b.name ? 1 : -1;
      }
    },
    {
      Header: (col) => {
        return (
          <div className="LogsTable_Header_OuterDiv">
            <IconAndText
              img={IconLoader.icon_table_sort}
              hoverImg={IconLoader.icon_table_sort_hover}
              text={'Message'}
              reverse
              onClick={async () => {
                reactTableSortOnClick(parent, 'log_message');
                updateSortedLocalLogList(parent, col.data);
              }}
            />
          </div>
        );
      },
      id: 'log_message',
      resizable: false,
      minWidth: 170,
      accessor: (log) => {
        return createLogObjForSorting(log);
      },
      Cell: (row) => {
        const { message } = row.value;
        return (
          <div className={classnames({ LogsTable_cellWrapper: true, LogsTable_Message: true })} data-tip={message}>
            {message}
          </div>
        );
      },
      sortMethod: (a, b) => {
        if (a.message !== b.message) return a.message > b.message ? 1 : -1;
        if (a.timeSinceBoot !== b.timeSinceBoot) return a.timeSinceBoot > b.timeSinceBoot ? 1 : -1;
        if (a.name !== b.name) return a.name > b.name ? 1 : -1;
        if (a.category !== b.category) return a.category > b.category ? 1 : -1;
      }
    },
    {
      Header: (col) => {
        return (
          <div className="LogsTable_Header_OuterDiv">
            <SvgAF
              img={IconLoader.icon_log_expand_rows}
              hover={IconLoader.icon_log_expand_rows_hover}
              tooltip={'Expand all rows'}
              onClick={() => {
                let expandAll = parent.expandAll;
                let trGroups = window.document.querySelectorAll('.rt-tr-group');
                trGroups.forEach((group) => {
                  let trGroup = group.firstChild;
                  let cellWrappers = trGroup.querySelectorAll('.LogsTable_cellWrapper');
                  let message = trGroup.querySelectorAll('.LogsTable_Message')[0];
                  let svg = trGroup.getElementsByClassName('SvgAF') && trGroup.getElementsByClassName('SvgAF')[0];

                  trGroup.style.transition = 'all 0.5s ease';

                  cellWrappers.forEach((cell) => {
                    if (expandAll) {
                      cell.style.whiteSpace = 'break-spaces';
                      cell.style.wordBreak = 'break-all';
                    } else {
                      cell.style.whiteSpace = 'nowrap';
                      cell.style.wordBreak = 'none';
                    }
                  });

                  if (message && svg) {
                    if (expandAll) {
                      trGroup.style.height = '120px';
                      message.style.whiteSpace = 'break-spaces';
                      svg.getElementsByTagName('img')[0].style.transform = 'rotate(180deg)';
                    } else {
                      trGroup.style.height = '40px';
                      message.style.whiteSpace = 'nowrap';
                      svg.getElementsByTagName('img')[0].style.transform = '';
                    }
                  }
                });
                parent.expandAll = !expandAll;
              }}
            />
          </div>
        );
      },
      id: 'log_expand_button',
      minWidth: 10,
      style: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '12px'
      },
      resizable: false,
      filterable: false,
      sortable: false,
      accessor: (log) => {
        return {
          length: log.message.length
        };
      },
      Cell: (row) => {
        let { length } = row.value;
        let viewIndex = row.viewIndex;
        if (length > 64) {
          return (
            <div className="LogsTable_cellWrapper">
              <SvgAF
                img={IconLoader.icon_log_expand_cell}
                hover={IconLoader.icon_log_expand_cell_hover}
                tooltip={'Expand row'}
                onClick={async () => {
                  let trGroup = window.document.querySelectorAll('.rt-tr-group')[viewIndex].firstChild;
                  let cellWrappers = trGroup.querySelectorAll('.LogsTable_cellWrapper');
                  let message = trGroup.querySelectorAll('.LogsTable_Message')[0];
                  let currentRowHeight = trGroup.style.height;
                  let arrowImg = trGroup.getElementsByClassName('SvgAF')[0].getElementsByTagName('img')[0];

                  cellWrappers.forEach((cell) => {
                    if (cell.style.whiteSpace === 'break-spaces') {
                      cell.style.whiteSpace = 'nowrap';
                      cell.style.wordBreak = 'none';
                    } else {
                      cell.style.whiteSpace = 'break-spaces';
                      cell.style.wordBreak = 'break-all';
                    }
                  });

                  if (currentRowHeight === '120px') {
                    trGroup.style.height = '40px';
                    trGroup.style.transition = 'all 0.5s ease';
                    message.style.whiteSpace = 'nowrap';
                  } else if (currentRowHeight === '40px') {
                    trGroup.style.height = '120px';
                    trGroup.style.transition = 'all 0.5s ease';
                    message.style.whiteSpace = 'break-spaces';
                  }

                  if (!arrowImg.style.transform) {
                    arrowImg.style.transform = 'rotate(180deg)';
                  } else if (arrowImg.style.transform === 'rotate(180deg)') {
                    arrowImg.style.transform = '';
                  } else {
                    arrowImg.style.transform = 'rotate(180deg)';
                  }
                }}
              />
            </div>
          );
        } else {
          return null;
        }
      }
    }
  ];
};
